import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Events that count as the user still being there. `scroll` is captured because it does not bubble to
 * window from a scrollable region, and `wheel` covers reading a long grid without ever clicking.
 */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * How often the deadline is checked. One second keeps the warning countdown exact; each tick is a
 * single `Date.now()` comparison, and React only re-renders when the displayed second changes.
 */
const TICK_MS = 1_000;

interface InactivityTimeout {
  /** Milliseconds left before sign-out while the warning is due, otherwise null. */
  readonly remainingMs: number | null;
  /** Treats the user as active again and dismisses the warning. */
  staySignedIn: () => void;
}

/**
 * Calls `onTimeout` once the user has been inactive for `timeoutMinutes`, and not again until activity
 * resumes.
 *
 * Activity is recorded as a timestamp and the deadline is checked on a timer, rather than resetting a
 * timer on every event: a provider scrolling a claims grid would otherwise reschedule hundreds of times
 * a second. A tab left in the background still times out, because the check is against wall-clock time
 * rather than against elapsed timer ticks.
 */
export function useInactivityTimeout(
  timeoutMinutes: number,
  onTimeout: () => void,
  enabled = true,
  warningMinutes = 0,
): InactivityTimeout {
  const lastActivityRef = useRef(0);
  const firedRef = useRef(false);
  const warningRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const staySignedIn = useCallback((): void => {
    lastActivityRef.current = Date.now();
    firedRef.current = false;
    warningRef.current = false;
    setRemainingMs(null);
  }, []);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    lastActivityRef.current = Date.now();
    firedRef.current = false;
    warningRef.current = false;
    const timeoutMs = timeoutMinutes * 60_000;
    // Never longer than the timeout itself, or the warning would be due before any inactivity.
    const warningMs = Math.min(Math.max(warningMinutes, 0) * 60_000, timeoutMs);

    const markActive = (): void => {
      // Once the warning is up, only the Stay Signed In button counts: a stray mouse move or the
      // click that lands on the overlay must not silently cancel a logout the user has been told about.
      if (warningRef.current) {
        return;
      }

      lastActivityRef.current = Date.now();
      firedRef.current = false;
    };

    const checkDeadline = (): void => {
      const idleMs = Date.now() - lastActivityRef.current;

      if (idleMs >= timeoutMs) {
        warningRef.current = false;
        setRemainingMs(null);

        // Latched: the deadline stays passed until the user comes back, and a consumer that does not
        // unmount must not be called once per tick forever.
        if (!firedRef.current) {
          firedRef.current = true;
          onTimeoutRef.current();
        }
        return;
      }

      const warn = warningMs > 0 && idleMs >= timeoutMs - warningMs;
      warningRef.current = warn;
      // Whole seconds, rounded up, so the state only changes once per second and the dialog never
      // shows 00:00 while there is still time left.
      setRemainingMs(warn ? Math.ceil((timeoutMs - idleMs) / 1000) * 1000 : null);
    };

    const stopWatchingActivity = watchActivity(markActive);
    const stopWatchingResume = watchResume(checkDeadline);
    const interval = window.setInterval(checkDeadline, TICK_MS);

    return (): void => {
      window.clearInterval(interval);
      stopWatchingActivity();
      stopWatchingResume();
    };
  }, [timeoutMinutes, warningMinutes, enabled]);

  return { remainingMs: enabled ? remainingMs : null, staySignedIn };
}

/**
 * Re-checks the deadline the moment a frozen document comes back.
 *
 * A tab restored from the back/forward cache resumes with the previous session's screen already painted
 * and its interval suspended, so waiting for the next tick would leave that data legible for up to
 * TICK_MS after the resume.
 */
function watchResume(checkDeadline: () => void): () => void {
  const onResume = (): void => {
    if (document.visibilityState === 'visible') {
      checkDeadline();
    }
  };

  window.addEventListener('pageshow', onResume);
  document.addEventListener('visibilitychange', onResume);

  return (): void => {
    window.removeEventListener('pageshow', onResume);
    document.removeEventListener('visibilitychange', onResume);
  };
}

/** Subscribes to the activity events and returns the matching unsubscribe. */
function watchActivity(markActive: () => void): () => void {
  for (const event of ACTIVITY_EVENTS) {
    window.addEventListener(event, markActive, { passive: true });
  }
  window.addEventListener('scroll', markActive, { passive: true, capture: true });

  return (): void => {
    for (const event of ACTIVITY_EVENTS) {
      window.removeEventListener(event, markActive);
    }
    window.removeEventListener('scroll', markActive, { capture: true });
  };
}
